import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Star } from "@phosphor-icons/react";
import { MaskedLine, Reveal, Overline } from "../components/Kinetic";
import { Seo } from "../components/Seo";
import { api, imgSrc } from "../lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function Guides() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles?limit=100").then((r) => setArticles(r.data.articles)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(articles.map((a) => a.category))], [articles]);
  const filtered = active === "All" ? articles : articles.filter((a) => a.category === active);
  const featured = articles.find((a) => a.is_2a_feature) || articles[0];
  const rest = filtered.filter((a) => a.slug !== featured?.slug || active !== "All");

  return (
    <main data-testid="guides-page">
      <Seo
        title="Buyback Guides"
        description="Practical, original guides on selling phones, laptops, tablets, watches, and consoles in Chicago — preparation, data privacy, valuation, and local options."
        path="/guides"
      />

      <section className="pt-32 md:pt-44">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Overline>The Content Hub</Overline>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>BUYBACK</MaskedLine>
            <MaskedLine delay={0.3}><span className="text-brand">GUIDES.</span></MaskedLine>
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[#4A4A4A]">
              Real answers to real questions Chicago sellers ask — preparation, privacy, valuation, and how
              buyback actually works. New guides added as they are worth publishing, never as filler.
            </p>
          </Reveal>
        </div>
      </section>

      {featured && (
        <section className="mt-16 md:mt-20">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <Link to={`/guides/${featured.slug}`} data-testid="featured-article-card"
                className="group grid overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:shadow-[0_24px_50px_rgb(0,0,0,0.1)] lg:grid-cols-2">
                <div className="h-64 overflow-hidden lg:h-auto">
                  <img src={imgSrc(featured.image_url)} alt={featured.image_alt} loading="eager"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-col justify-center p-8 md:p-12">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-brand">
                    {featured.is_2a_feature && <Star size={14} weight="fill" className="text-cash" />}
                    {featured.is_2a_feature ? "Local Business Feature" : "Featured Guide"}
                  </p>
                  <h2 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight text-black group-hover:text-brand md:text-3xl">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-[#4A4A4A]">{featured.excerpt}</p>
                  <p className="mt-5 text-xs font-medium uppercase tracking-[0.2em] text-[#8A8A8A]">{fmt(featured.published_at)}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">
                    Read the guide
                    <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-wrap gap-2.5" data-testid="guide-category-filters">
            {categories.map((c) => (
              <button key={c} onClick={() => setActive(c)} data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-all duration-300 ${
                  active === c ? "bg-navy text-white" : "border border-black/15 text-black/70 hover:border-navy hover:text-navy"
                }`}>
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-3xl bg-[#F5F5F7]" />)}
            </div>
          ) : (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((g, i) => (
                <Reveal key={g.slug} delay={(i % 3) * 0.07}>
                  <Link to={`/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                    <div className="h-52 overflow-hidden">
                      <img src={imgSrc(g.image_url)} alt={g.image_alt} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{g.category}</p>
                        <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-[#8A8A8A]">{fmt(g.published_at)}</p>
                      </div>
                      <h3 className="mt-3 font-display text-lg font-bold leading-snug tracking-tight text-black group-hover:text-brand">{g.title}</h3>
                      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#4A4A4A]">{g.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
